import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("jobseeker");
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await register({ ...form, role });
      toast.success("Kayıt başarılı, hoş geldiniz!");
      navigate(u.role === "employer" ? "/isveren" : "/panel");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Kaydol | isbul.org" description="isbul.org'a ücretsiz üye olun. İş arayan veya işveren olarak kaydolun." canonical="/kayit" />
      <Header />
      <div className="max-w-md mx-auto px-4 py-12">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <h1 className="text-2xl font-bold text-[#1f222d]">Hesap Oluştur</h1>
          <p className="text-slate-500 text-sm mt-1">Ücretsiz üye olun ve hemen başlayın.</p>

          <div className="grid grid-cols-2 gap-3 mt-6">
            <button type="button" data-testid="role-jobseeker" onClick={() => setRole("jobseeker")} className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${role === "jobseeker" ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
              <User size={22} className={role === "jobseeker" ? "text-blue-600" : "text-slate-400"} />
              <span className={`text-sm font-medium ${role === "jobseeker" ? "text-blue-700" : "text-[#1f222d]"}`}>İş Arayan</span>
            </button>
            <button type="button" data-testid="role-employer" onClick={() => setRole("employer")} className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${role === "employer" ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
              <Building2 size={22} className={role === "employer" ? "text-blue-600" : "text-slate-400"} />
              <span className={`text-sm font-medium ${role === "employer" ? "text-blue-700" : "text-[#1f222d]"}`}>İşveren</span>
            </button>
          </div>

          {err && <div data-testid="register-error" className="mt-4 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{err}</div>}

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <label className="text-sm font-medium text-[#1f222d]">{role === "employer" ? "Yetkili Adı Soyadı" : "Ad Soyad"}</label>
              <input data-testid="register-name" required value={form.name} onChange={set("name")} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-blue-400 text-[#1f222d]" placeholder="Adınız Soyadınız" />
            </div>
            {role === "employer" && (
              <div>
                <label className="text-sm font-medium text-[#1f222d]">Şirket Adı</label>
                <input data-testid="register-company" required value={form.company_name} onChange={set("company_name")} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-blue-400 text-[#1f222d]" placeholder="Şirketinizin adı" />
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-[#1f222d]">E-posta</label>
              <input data-testid="register-email" type="email" required value={form.email} onChange={set("email")} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-blue-400 text-[#1f222d]" placeholder="ornek@mail.com" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1f222d]">Şifre</label>
              <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-blue-400 text-[#1f222d]" placeholder="En az 6 karakter" />
            </div>
            <Button data-testid="register-submit" disabled={busy} type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-6 text-base font-semibold">
              {busy ? "Kaydolunuyor..." : "Kaydol"}
            </Button>
          </form>

          <p className="text-sm text-slate-500 text-center mt-6">
            Zaten hesabınız var mı? <Link to="/giris" className="text-blue-600 font-semibold hover:underline">Giriş Yap</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
